import { EncuestaDTO, PreguntaDTO } from "@DTOS";
import { ExportadorEncuestasExcel } from "./ExportadorEncuestasExcel";
import { FilasExcel, obtenerColumnaMap } from "@shared/enums";
import * as XLSX from "xlsx";
import { Nilable } from "@shared/utils";
import { Inject, Injectable } from "@angular/core";
import { PreguntaExportador } from "@shared/clases/exportador";
import { PreguntaExportadorToken } from "@shared/tokens";

interface FilaExcel {
    [columna: string]: Nilable<string>;
}

@Injectable()
export class ExportadorEncuestasExcelImpl implements ExportadorEncuestasExcel {
    private static readonly HEADERS = ["##Pregunta #", "##Tipo de pregunta", "##Pregunta", "##Random",
        "##Referencia", "##Items", "##Pregunta Siguiente", "##Killer", "##Visible Si", "##Min/Max", "##Customer ID",
        "##Bloque ID"];
    private static readonly WIDTH_HEADERS = [
        { wch: 3 }, // Numero de pregunta
        { wch: 15 }, // Tipo de pregunta
        { wch: 50 }, // Texto de pregunta
        { wch: 3 }, // Random
        { wch: 6 }, // Referencia
        { wch: 25 }, // Items
        { wch: 3 }, // Pregunta siguiente
        { wch: 3 }, // Killer
        { wch: 15 }, // Condiciones
        { wch: 10 }, // Min/max
        { wch: 3 }, // Customer id
        { wch: 3 } // bloque id
    ];

    constructor(@Inject(PreguntaExportadorToken) private exportadores: PreguntaExportador[]) {}

    public exportar(encuestaDTO: EncuestaDTO): void {
        if (encuestaDTO.preguntas == null) {
            return;
        }
        const encuestaMap = this.exportarEncuesta(encuestaDTO);
        const encuestaArray = this.transformarMapAArray(encuestaMap);
        this.exportarAExcel(encuestaDTO, encuestaArray);
    }

    private transformarMapAArray(datosPreguntaMap: Map<FilasExcel, string>[]): FilaExcel[] {
        return datosPreguntaMap.map(preguntaMap => ({
            [obtenerColumnaMap(FilasExcel.PREGUNTA_ID)]: preguntaMap.get(FilasExcel.PREGUNTA_ID) ?? null,
            [obtenerColumnaMap(FilasExcel.TIPO_PREGUNTA)]: preguntaMap.get(FilasExcel.TIPO_PREGUNTA) ?? null,
            [obtenerColumnaMap(FilasExcel.TEXTO_PREGUNTA)]: preguntaMap.get(FilasExcel.TEXTO_PREGUNTA) ?? null,
            [obtenerColumnaMap(FilasExcel.ALEATORIO)]: preguntaMap.get(FilasExcel.ALEATORIO) ?? null,
            [obtenerColumnaMap(FilasExcel.REFERENCIA)]: preguntaMap.get(FilasExcel.REFERENCIA) ?? null,
            [obtenerColumnaMap(FilasExcel.ITEMS)]: preguntaMap.get(FilasExcel.ITEMS) ?? null,
            [obtenerColumnaMap(FilasExcel.PREGUNTA_SIGUIENTE)]: preguntaMap.get(FilasExcel.PREGUNTA_SIGUIENTE) ?? null,
            [obtenerColumnaMap(FilasExcel.KILLER)]: preguntaMap.get(FilasExcel.KILLER) ?? null,
            [obtenerColumnaMap(FilasExcel.VISIBLE_SI)]: preguntaMap.get(FilasExcel.VISIBLE_SI) ?? null,
            [obtenerColumnaMap(FilasExcel.MIN_MAX)]: preguntaMap.get(FilasExcel.MIN_MAX) ?? null,
            [obtenerColumnaMap(FilasExcel.CUSTOMER_ID)]: preguntaMap.get(FilasExcel.CUSTOMER_ID) ?? null,
            [obtenerColumnaMap(FilasExcel.BLOQUE_ID)]: preguntaMap.get(FilasExcel.BLOQUE_ID) ?? null
        }));
    }

    private exportarAExcel(encuesta: EncuestaDTO, datos: FilaExcel[]) {
        const wb = XLSX.utils.book_new();
        const preguntas = XLSX.utils.json_to_sheet(datos, {
            header: ExportadorEncuestasExcelImpl.HEADERS
        });
        preguntas["!cols"] = ExportadorEncuestasExcelImpl.WIDTH_HEADERS;
        XLSX.utils.book_append_sheet(wb, preguntas, "Combo");
        XLSX.writeFile(wb, `${encuesta.id} - ${encuesta.nombre}.xlsx`);
    }

    private exportarEncuesta(encuestaDTO: EncuestaDTO): Map<FilasExcel, string>[] {
        const preguntasIdIndice = new Map<string, string>();
        const itemsIdIndice = new Map<string, string>();
        const bloqueIds = new Map<number, number>();
        const filas: Map<FilasExcel, string>[] = [];
        encuestaDTO.preguntas!.forEach((pregunta, indice) => {
            const exportador =
                this.exportadores.find(preguntaExportador => preguntaExportador.puedeProcesar(pregunta.tipo));
            if (exportador == null) {
                return;
            }
            if (pregunta.idBloque != null && !bloqueIds.has(pregunta.idBloque)) {
                bloqueIds.set(pregunta.idBloque, indice + 1);
            }
            preguntasIdIndice.set(`${pregunta.id}`, `${indice + 1}`);
            this.cargarIndicesItems(pregunta, itemsIdIndice, `${indice + 1}`);
            filas.push(
                ...exportador.exportar({
                    pregunta,
                    indice: indice + 1,
                    itemsIdsAnteriores: itemsIdIndice,
                    bloqueIds
                })
            );
        });
        filas.forEach(fila => {
            const siguiente = fila.get(FilasExcel.PREGUNTA_SIGUIENTE)!;
            fila.set(FilasExcel.PREGUNTA_SIGUIENTE, preguntasIdIndice.get(siguiente)!);
        });
        return filas;
    }

    private cargarIndicesItems(pregunta: PreguntaDTO, mapIndices: Map<string, string>, indicePregunta: string) {
        if (pregunta.preguntas != null) {
            pregunta.preguntas.forEach(subpregunta => {
                subpregunta.items.forEach((item, indice) => {
                    mapIndices.set(`i${item.id}`, `${indicePregunta}.${pregunta.preguntas!.length + indice + 1}`);
                });
            });
            return;
        }
        pregunta.items.forEach((item, indice) => {
            mapIndices.set(`i${item.id}`, `${indicePregunta}.${indice + 1}`);
        });
    }
}
